import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import type { Card, Color, TagProfile, HierarchicalTagProfile, TagTreeNode } from '@mtg-deck-tool/shared';
import { getCardCategory, parseManaCost, isLand } from '@mtg-deck-tool/shared';
import { useSearchStore } from '../../stores/search-store.js';
import { useDeckStore } from '../../stores/deck-store.js';
import { api } from '../../lib/api-client.js';
import { SearchResultRow } from './SearchResultRow.js';
import { ManaOverviewPanel } from '../land-autofill/ManaOverviewPanel.js';
import type { ManaOverviewRow } from '../../lib/land-sim/karsten-types.js';
import { computeKarstenSources, computeKarstenTargets, expandHybridPipCounts } from '../../lib/land-sim/karsten-sources.js';
import { buildSimLandCardWithCategory } from '../../lib/land-sim/sim-selection.js';
import { LAND_CATALOG } from '../../data/land-catalog.js';

const TYPE_ORDER = ['planeswalkers', 'creatures', 'instants', 'sorceries', 'artifacts', 'enchantments', 'lands', 'battles', 'other'] as const;
const TYPE_LABELS: Record<string, string> = {
  planeswalkers: 'Planeswalkers',
  creatures: 'Creatures',
  instants: 'Instants',
  sorceries: 'Sorceries',
  artifacts: 'Artifacts',
  enchantments: 'Enchantments',
  lands: 'Lands',
  battles: 'Battles',
  other: 'Other',
};

/** Get card type category from a Card (wraps the DeckEntry-based function) */
function cardCategory(card: Card): string {
  return getCardCategory({ card, quantity: 1, isCommander: false });
}

/** Group cards by type category in the defined order. If quantityMap is provided, totalCount sums quantities. */
function groupByType(cards: Card[], quantityMap?: Map<string, number>): Array<{ type: string; label: string; cards: Card[]; totalQuantity?: number }> {
  const groups = new Map<string, Card[]>();
  for (const card of cards) {
    const cat = cardCategory(card);
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat)!.push(card);
  }
  return TYPE_ORDER
    .filter((t) => groups.has(t))
    .map((t) => {
      const g = groups.get(t)!;
      const totalQuantity = quantityMap ? g.reduce((sum, c) => sum + (quantityMap.get(c.id) ?? 1), 0) : undefined;
      return { type: t, label: TYPE_LABELS[t] ?? t, cards: g, ...(totalQuantity !== undefined && { totalQuantity }) };
    });
}

/**
 * Collect all card names for a tag slug, including descendant tags in the hierarchy.
 * Falls back to flat profile if hierarchy is unavailable.
 */
function collectCardNamesForTag(
  slug: string,
  flatProfile: TagProfile | null,
  hierarchy: HierarchicalTagProfile | null,
): Set<string> {
  const names = new Set<string>();

  // Always check flat profile for direct matches
  if (flatProfile) {
    const entry = flatProfile.entries.find((e) => e.tag.slug === slug);
    if (entry) for (const n of entry.cardNames) names.add(n.toLowerCase());
  }

  // Walk hierarchy to find descendant card names
  if (hierarchy) {
    const findNode = (nodes: TagTreeNode[]): TagTreeNode | undefined => {
      for (const node of nodes) {
        if (node.slug === slug) return node;
        const found = findNode(node.children);
        if (found) return found;
      }
      return undefined;
    };

    const collectFromNode = (node: TagTreeNode) => {
      // Get direct card names from flat profile for this node's slug
      if (flatProfile) {
        const entry = flatProfile.entries.find((e) => e.tag.slug === node.slug);
        if (entry) for (const n of entry.cardNames) names.add(n.toLowerCase());
      }
      // Recurse into children
      for (const child of node.children) collectFromNode(child);
    };

    const targetNode = findNode(hierarchy.roots);
    if (targetNode) collectFromNode(targetNode);
  }

  return names;
}

/**
 * SearchResultsArea — sectioned text-based results replacing the image grid.
 *
 * Sections:
 *   1. DeckMatchSection — deck cards matching the query
 *   2. SideboardMatchSection — sideboard cards matching the query
 *   3. NewResultsSection — remaining Scryfall results
 */

/** A chunk is a (possibly partial) type group assigned to a column */
interface ColumnChunk {
  type: string;
  label: string;
  cards: Card[];
  /** Total cards in this type across all chunks (for display). Only set on the first chunk. */
  totalCount?: number;
  /** Whether this is the first chunk of this type (shows the header) */
  isFirst: boolean;
}

/**
 * Distribute groups across N columns, splitting groups when needed.
 * Fills columns left-to-right. When a group doesn't fit the remaining
 * target for the current column, it's split so the column hits its target.
 *
 * Examples (3 cols):
 *   [13 creatures, 1 artifact] → [5 creatures], [5 creatures], [3 creatures + 1 artifact]
 *   [13 creatures] → [5], [4], [4]
 *   [9 creatures, 6 artifacts, 6 enchantments] → [9], [6], [6]
 *   [8 creatures, 1 enchantment] → [3], [3], [2 + 1]
 */
function distributeToColumns(
  groups: Array<{ type: string; label: string; cards: Card[] }>,
  numCols: number,
): ColumnChunk[][] {
  if (numCols <= 1) return [groups.map((g) => ({ type: g.type, label: g.label, cards: g.cards, totalCount: (g as { totalQuantity?: number }).totalQuantity ?? g.cards.length, isFirst: true }))];

  const totalItems = groups.reduce((sum, g) => sum + g.cards.length, 0);
  const target = Math.ceil(totalItems / numCols);

  // Track which types have had their first chunk emitted
  const seenTypes = new Set<string>();
  // Pre-compute total count per type (use totalQuantity if available for correct multi-copy counts)
  const typeTotals = new Map<string, number>();
  for (const g of groups) typeTotals.set(g.type, (g as { totalQuantity?: number }).totalQuantity ?? g.cards.length);

  const columns: ColumnChunk[][] = [];
  let currentCol: ColumnChunk[] = [];
  let currentHeight = 0;
  let colIdx = 0;

  for (const group of groups) {
    let remaining = [...group.cards];

    const makeChunk = (cards: Card[]): ColumnChunk => {
      const isFirst = !seenTypes.has(group.type);
      if (isFirst) seenTypes.add(group.type);
      const totalCount = isFirst ? typeTotals.get(group.type) : undefined;
      return {
        type: group.type,
        label: group.label,
        cards,
        isFirst,
        ...(totalCount !== undefined && { totalCount }),
      };
    };

    while (remaining.length > 0) {
      const spaceLeft = target - currentHeight;
      const colsLeft = numCols - colIdx;

      // Column is full — move to next before processing
      if (spaceLeft <= 0 && colsLeft > 1) {
        columns.push(currentCol);
        currentCol = [];
        currentHeight = 0;
        colIdx++;
        continue;
      }

      if (colsLeft <= 1) {
        // Last column: take everything
        currentCol.push(makeChunk(remaining));
        currentHeight += remaining.length;
        remaining = [];
      } else if (remaining.length <= spaceLeft) {
        // Fits in current column
        currentCol.push(makeChunk(remaining));
        currentHeight += remaining.length;
        remaining = [];
      } else if (currentHeight === 0 && remaining.length <= target * 1.3) {
        // Group slightly over target, column is empty — keep whole
        currentCol.push(makeChunk(remaining));
        currentHeight += remaining.length;
        remaining = [];
        columns.push(currentCol);
        currentCol = [];
        currentHeight = 0;
        colIdx++;
      } else {
        // Split: fill to target, rest goes to next column
        const take = Math.max(1, spaceLeft);
        currentCol.push(makeChunk(remaining.slice(0, take)));
        currentHeight += take;
        remaining = remaining.slice(take);
        columns.push(currentCol);
        currentCol = [];
        currentHeight = 0;
        colIdx++;
      }
    }
  }

  // Push the last column
  if (currentCol.length > 0) {
    columns.push(currentCol);
  }

  // Pad with empty columns if needed
  while (columns.length < numCols) {
    columns.push([]);
  }

  return columns;
}

/**
 * Renders groups distributed across responsive columns.
 * Groups are split across columns to balance heights.
 * Each chunk gets its type header so split groups are still labeled.
 */
function ColumnLayout({ groups, renderChunk }: {
  groups: Array<{ type: string; label: string; cards: Card[] }>;
  renderChunk: (chunk: ColumnChunk) => React.ReactNode;
}) {
  const [numCols, setNumCols] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.offsetWidth;
      setNumCols(w >= 1100 ? 3 : w >= 700 ? 2 : 1);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const columns = distributeToColumns(groups, numCols);

  return (
    <div ref={containerRef} className="grid" style={{ gridTemplateColumns: `repeat(${numCols}, 1fr)` }}>
      {columns.map((col, i) => (
        <div key={i} className="min-w-0">
          {col.map((chunk, j) => (
            <div key={`${chunk.type}-${j}`}>
              {/* Spacer for continuation chunks at top of column — aligns with headers in other columns */}
              {j === 0 && !chunk.isFirst && (
                <div className="px-3 py-1.5 text-[11px]">&nbsp;</div>
              )}
              {renderChunk(chunk)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Simple column layout for flat card lists (no type groups).
 * Splits cards into roughly equal columns.
 */
function FlatColumnLayout({ cards, renderCard }: {
  cards: Card[];
  renderCard: (card: Card) => React.ReactNode;
}) {
  const [numCols, setNumCols] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.offsetWidth;
      setNumCols(w >= 1100 ? 3 : w >= 700 ? 2 : 1);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Split cards into roughly equal columns
  const perCol = Math.ceil(cards.length / numCols);
  const columns: Card[][] = [];
  for (let i = 0; i < numCols; i++) {
    columns.push(cards.slice(i * perCol, (i + 1) * perCol));
  }

  return (
    <div ref={containerRef} className="grid" style={{ gridTemplateColumns: `repeat(${numCols}, 1fr)` }}>
      {columns.map((col, i) => (
        <div key={i} className="min-w-0">
          {col.map((card) => renderCard(card))}
        </div>
      ))}
    </div>
  );
}
export function SearchResultsArea() {
  const results = useSearchStore((s) => s.results);
  const isLoading = useSearchStore((s) => s.isLoading);
  const error = useSearchStore((s) => s.error);
  const userInput = useSearchStore((s) => s.userInput);
  const activeTagSlugs = useSearchStore((s) => s.activeTagSlugs);
  const activeRecBucket = useSearchStore((s) => s.activeRecBucket);
  const currentPage = useSearchStore((s) => s.currentPage);

  const entries = useDeckStore((s) => s.entries);
  const sideboard = useDeckStore((s) => s.sideboard);
  const tagProfile = useDeckStore((s) => s.tagProfile);
  const tagHierarchy = useDeckStore((s) => s.tagHierarchy);
  const selectedDirectionTag = useDeckStore((s) => s.selectedDirectionTag);
  const moxfieldDeckId = useDeckStore((s) => s.moxfieldDeckId);
  const recommendationsCache = useDeckStore((s) => s.recommendationsCache);
  const colorIdentity = useDeckStore((s) => s.colorIdentity);

  // Mana overview for existing deck (refresh button triggers recalc)
  const [manaRefreshKey, setManaRefreshKey] = useState(0);
  const deckManaOverview = useMemo((): ManaOverviewRow[] => {
    void manaRefreshKey;
    if (colorIdentity.length === 0) return [];

    const ALL_COLORS: Color[] = ['W', 'U', 'B', 'R', 'G'];
    const nameToCycleEntry = new Map<string, { entry: any; cycle: any }>();
    for (const cycle of LAND_CATALOG) {
      for (const entry of cycle.entries) {
        nameToCycleEntry.set(entry.name, { entry, cycle });
      }
    }

    const simLands: Array<any> = [];
    for (const e of entries) {
      if (e.isCommander) continue;
      if (!isLand(e.card)) continue;
      const found = nameToCycleEntry.get(e.card.name);
      if (found) {
        const simCard = buildSimLandCardWithCategory(found.entry, found.cycle);
        for (let i = 0; i < e.quantity; i++) simLands.push(simCard);
      } else {
        const oracle = (e.card.oracleText ?? '').toLowerCase();
        const isAnyColor = oracle.includes('mana of any color') || oracle.includes('any combination of colors');
        let producedColors: Set<Color>;
        if (isAnyColor) {
          producedColors = new Set<Color>();
        } else {
          const colors = new Set<Color>();
          const symbolMatches = oracle.matchAll(/\{([wubrgWUBRG])\}/g);
          for (const m of symbolMatches) {
            const sym = m[1]!.toUpperCase();
            if (ALL_COLORS.includes(sym as Color)) colors.add(sym as Color);
          }
          producedColors = colors.size > 0 ? colors : new Set<Color>(['C' as Color]);
        }
        for (let i = 0; i < e.quantity; i++) {
          simLands.push({
            name: e.card.name,
            producedColors,
            basicLandTypes: new Set(),
            isLand: true,
            alwaysUntapped: true,
            alwaysTapped: false,
            isFetch: false,
            isBasic: false,
            isVerge: false,
            _category: 'rainbow',
          });
        }
      }
    }

    const totalLandCount = simLands.length;
    if (totalLandCount === 0) return [];

    const landSources = computeKarstenSources(simLands, colorIdentity as Color[]);

    const rawPips = new Map<string, number>();
    for (const e of entries) {
      if (e.isCommander) continue;
      if (isLand(e.card)) continue;
      if (!e.card.manaCost) continue;
      const tokens = parseManaCost(e.card.manaCost);
      for (const token of tokens) {
        const sym = token.slice(1, -1);
        if (ALL_COLORS.includes(sym as Color)) {
          rawPips.set(sym, (rawPips.get(sym) ?? 0) + e.quantity);
        }
      }
    }
    const pipCounts = expandHybridPipCounts(rawPips);
    const totalPips = Array.from(pipCounts.values()).reduce((s, v) => s + v, 0);
    const karstenTargets = computeKarstenTargets(colorIdentity.length, pipCounts);

    return (colorIdentity as Color[]).map((color) => {
      const pips = pipCounts.get(color) ?? 0;
      const ls = landSources.get(color) ?? 0;
      const target = karstenTargets.targets.get(color) ?? 0;
      const pipDemandPct = totalPips > 0 ? (pips / totalPips) * 100 : 0;
      const productionPct = totalLandCount > 0 ? (ls / totalLandCount) * 100 : 0;
      const diffPct = productionPct - pipDemandPct;
      return {
        color,
        pipDemandPct: Math.round(pipDemandPct * 10) / 10,
        productionPct: Math.round(productionPct * 10) / 10,
        diffPct: Math.round(diffPct * 10) / 10,
        landSources: ls,
        nonLandSources: 0,
        target,
      };
    });
  }, [entries, colorIdentity, manaRefreshKey]);

  // Resolve high synergy card names to full Card objects
  const [highSynergyCards, setHighSynergyCards] = useState<Card[]>([]);
  useEffect(() => {
    if (!selectedDirectionTag || selectedDirectionTag.cardNames.length === 0) {
      setHighSynergyCards([]);
      return;
    }
    let cancelled = false;
    api.resolveCards(selectedDirectionTag.cardNames).then(({ found }) => {
      if (!cancelled) setHighSynergyCards(found);
    }).catch(() => {
      if (!cancelled) setHighSynergyCards([]);
    });
    return () => { cancelled = true; };
  }, [selectedDirectionTag]);

  // Partition results into deck matches, sideboard matches, and new results
  const { deckMatches, sideboardMatches, newResults } = useMemo(() => {
    const empty = { deckMatches: [] as Card[], sideboardMatches: [] as Card[], newResults: [] as Card[] };

    if (!results || results.cards.length === 0) return empty;

    const sideboardOracleIds = new Set(sideboard.map((c) => c.card.oracleId));

    // When a rectag bucket is active, the result list IS the bucket — partitioning
    // it into "deck matches" / "recommendations" sections is noisy. Just split out
    // anything in the user's sideboard and treat everything else as flat results.
    if (activeRecBucket !== null) {
      const sideboardCards = results.cards.filter((c) => sideboardOracleIds.has(c.oracleId));
      const sbIds = new Set(sideboardCards.map((c) => c.oracleId));
      const rest = results.cards.filter((c) => !sbIds.has(c.oracleId));
      return { deckMatches: [], sideboardMatches: sideboardCards, newResults: rest };
    }

    const deckOracleIds = new Set(entries.map((e) => e.card.oracleId));

    // Only compute deck/sideboard matches on page 1
    if (currentPage !== 1) {
      return { deckMatches: [], sideboardMatches: [], newResults: results.cards };
    }

    // --- Deck matches ---
    let deckCards: Card[] = [];

    // When tag filters are active, get matching deck cards from tag profile + hierarchy
    if (activeTagSlugs.length > 0 && tagProfile) {
      const matchingNames = new Set<string>();
      for (const slug of activeTagSlugs) {
        const names = collectCardNamesForTag(slug, tagProfile, tagHierarchy ?? null);
        for (const n of names) matchingNames.add(n);
      }
      if (matchingNames.size > 0) {
        deckCards = entries
          .filter((e) => matchingNames.has(e.card.name.toLowerCase()))
          .map((e) => e.card);
      }
    }

    // Also include any deck cards that Scryfall returned on this page
    const pinnedOracleIds = new Set(deckCards.map((c) => c.oracleId));
    const additionalDeckCards = results.cards.filter(
      (card) => deckOracleIds.has(card.oracleId) && !pinnedOracleIds.has(card.oracleId),
    );
    const allDeckCards = [...deckCards, ...additionalDeckCards];
    const allDeckOracleIds = new Set(allDeckCards.map((c) => c.oracleId));

    // --- Sideboard matches ---
    const sideboardCards = results.cards.filter(
      (card) => sideboardOracleIds.has(card.oracleId) && !allDeckOracleIds.has(card.oracleId),
    );
    const allSideboardOracleIds = new Set(sideboardCards.map((c) => c.oracleId));

    // --- New results (everything else) ---
    const remaining = results.cards.filter(
      (card) => !allDeckOracleIds.has(card.oracleId) && !allSideboardOracleIds.has(card.oracleId),
    );

    return {
      deckMatches: allDeckCards,
      sideboardMatches: sideboardCards,
      newResults: remaining,
    };
  }, [results, currentPage, entries, sideboard, activeTagSlugs, activeRecBucket, tagProfile, tagHierarchy]);

  // Build set of recommended oracle IDs from both sources
  const deckOracleIdsSet = useMemo(() => new Set(entries.map((e) => e.card.oracleId)), [entries]);

  // 1. EDHREC high synergy cards (when a direction tag is active)
  const showSynergy = highSynergyCards.length > 0 && selectedDirectionTag && activeTagSlugs.includes(selectedDirectionTag.slug);
  const filteredSynergyCards = useMemo(
    () => showSynergy ? highSynergyCards.filter((c) => !deckOracleIdsSet.has(c.oracleId)) : [],
    [showSynergy, highSynergyCards, deckOracleIdsSet],
  );
  const synergyOracleIds = useMemo(() => new Set(filteredSynergyCards.map((c) => c.oracleId)), [filteredSynergyCards]);

  // 2. Recommander.cards "Top picks" — main flat list filtered to non-generic
  //    (staples are surfaced separately in the Discover tab).
  const recommanderOracleIds = useMemo(() => {
    if (!moxfieldDeckId) return new Set<string>();
    const data = recommendationsCache.get(moxfieldDeckId) as
      | { recommendations?: Array<{ oracle_id: string; generic: boolean }> }
      | undefined;
    if (!data?.recommendations) return new Set<string>();
    return new Set(data.recommendations.filter((r) => !r.generic).map((r) => r.oracle_id));
  }, [moxfieldDeckId, recommendationsCache]);

  // Combined: search results that are in EITHER recommendation source, not in deck.
  // Skip this bubble-up entirely when a rectag bucket is active — every card in
  // the result list is already a recommander pick, so a separate "Recommendations"
  // section adds noise without info.
  const { recommendedResults, filteredNewResults } = useMemo(() => {
    if (activeRecBucket !== null) {
      return { recommendedResults: [] as Card[], filteredNewResults: newResults };
    }

    const recommended: Card[] = [];
    const rest: Card[] = [];

    for (const card of newResults) {
      if (synergyOracleIds.has(card.oracleId) || recommanderOracleIds.has(card.oracleId)) {
        recommended.push(card);
      } else {
        rest.push(card);
      }
    }

    // Also add synergy cards that aren't in the search results (they come from a separate resolve)
    const resultOracleIds = new Set(newResults.map((c) => c.oracleId));
    for (const card of filteredSynergyCards) {
      if (!resultOracleIds.has(card.oracleId)) {
        recommended.push(card);
      }
    }

    return { recommendedResults: recommended, filteredNewResults: rest };
  }, [newResults, synergyOracleIds, recommanderOracleIds, filteredSynergyCards, activeRecBucket]);

  // --- Loading state ---
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Searching...</p>
        </div>
      </div>
    );
  }

  // --- Error state ---
  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // --- No search active: show full deck as browsable text rows ---
  if (!results) {
    if (activeTagSlugs.length > 0) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-600 text-sm">Loading tag results...</p>
        </div>
      );
    }

    const allDeckCards = entries.map((e) => e.card);
    const totalDeckCount = entries.reduce((sum, e) => sum + e.quantity, 0);
    const deckQuantityMap = new Map(entries.map((e) => [e.card.id, e.quantity]));
    const allSideboardCards = sideboard.map((c) => c.card);


    // Count MDFCs whose back face is a land (for the Lands header display)
    const mdfcLandCount = entries.filter((e) => {
      if (e.isCommander) return false;
      const tl = e.card.typeLine;
      if (!tl.includes('//')) return false;
      const back = tl.split('//')[1]?.trim().toLowerCase() ?? '';
      const front = tl.split('//')[0]?.trim().toLowerCase() ?? '';
      return back.includes('land') && !front.includes('land');
    }).reduce((sum, e) => sum + e.quantity, 0);

    if (allDeckCards.length === 0 && allSideboardCards.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center space-y-2 max-w-xs">
            <p className="text-gray-600 text-sm">Search for cards to add to your deck.</p>
            <p className="text-gray-700 text-xs">
              Results are pre-filtered to your commander's color identity and Commander legality.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 overflow-y-auto">
        {allDeckCards.length > 0 && (
          <div>
            <div className="px-3 py-1.5 text-[10px] font-semibold text-orange-400/80 uppercase tracking-wider bg-gray-900/60 border-b border-gray-800/50">
              Your Deck ({totalDeckCount})
            </div>
            <ColumnLayout
              groups={groupByType(allDeckCards, deckQuantityMap)}
              renderChunk={(chunk) => (
                <div>
                  {chunk.isFirst && (
                    <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-400 border-b border-gray-700/50">
                      {chunk.label} <span className="text-gray-600 font-normal">
                        ({chunk.totalCount}{chunk.type === 'lands' && mdfcLandCount > 0 ? `, ${(chunk.totalCount ?? 0) + mdfcLandCount} incl. MDFC` : ''})
                      </span>
                    </div>
                  )}
                  {chunk.cards.map((card) => (
                    <SearchResultRow key={`deck-${card.id}`} card={card} />
                  ))}
                </div>
              )}
            />
          </div>
        )}

        {/* Mana overview for existing deck */}
        {deckManaOverview.length > 0 && (
          <div className="border-b border-gray-800/50">
            <div className="flex items-center justify-between px-4 pt-2">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                Current Mana Base
              </span>
              <button
                type="button"
                onClick={() => setManaRefreshKey((k) => k + 1)}
                className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors"
                title="Recalculate"
              >
                ⟳
              </button>
            </div>
            <ManaOverviewPanel rows={deckManaOverview} />
          </div>
        )}

        {allSideboardCards.length > 0 && (
          <div>
            <div className="px-3 py-1.5 text-[10px] font-semibold text-blue-400/80 uppercase tracking-wider bg-gray-900/60 border-b border-gray-800/50">
              Sideboard ({allSideboardCards.length})
            </div>
            <FlatColumnLayout
              cards={allSideboardCards}
              renderCard={(card) => <SearchResultRow key={`sideboard-${card.id}`} card={card} />}
            />
          </div>
        )}
      </div>
    );
  }

  // --- No results state ---
  if (results.cards.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="text-center space-y-1">
          <p className="text-gray-500 text-sm">No cards found.</p>
          <p className="text-gray-700 text-xs">Try adjusting your search or filters.</p>
          {userInput && (
            <p className="text-gray-800 text-[10px] font-mono mt-2 break-all">
              query: {userInput}
            </p>
          )}
        </div>
      </div>
    );
  }

  // --- Results with sections ---
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="text-xs text-gray-600 px-3 pt-3 pb-1">
        {results.totalCards.toLocaleString()} result{results.totalCards !== 1 ? 's' : ''}
      </div>

      {/* Deck match section — grouped by card type */}
      {deckMatches.length > 0 && (
        <div>
          <div className="px-3 py-1.5 text-[10px] font-semibold text-orange-400/80 uppercase tracking-wider bg-gray-900/60 border-b border-gray-800/50">
            In Your Deck ({deckMatches.length})
          </div>
          <ColumnLayout
            groups={groupByType(deckMatches)}
            renderChunk={(chunk) => (
              <div>
                {chunk.isFirst && (
                  <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-400 border-b border-gray-700/50">
                    {chunk.label} <span className="text-gray-600 font-normal">({chunk.totalCount})</span>
                  </div>
                )}
                {chunk.cards.map((card) => (
                  <SearchResultRow key={`deck-${card.id}`} card={card} />
                ))}
              </div>
            )}
          />
        </div>
      )}

      {/* Recommendations — cards from search that overlap with EDHREC synergy or recommander.cards */}
      {recommendedResults.length > 0 && (
        <div>
          <div className="px-3 py-1.5 text-[10px] font-semibold text-green-400/80 uppercase tracking-wider bg-gray-900/60 border-b border-gray-800/50">
            Recommendations ({recommendedResults.length})
          </div>
          <FlatColumnLayout
            cards={recommendedResults}
            renderCard={(card) => <SearchResultRow key={`rec-${card.id}`} card={card} />}
          />
        </div>
      )}

      {/* Sideboard match section */}
      {sideboardMatches.length > 0 && (
        <div>
          <div className="px-3 py-1.5 text-[10px] font-semibold text-blue-400/80 uppercase tracking-wider bg-gray-900/60 border-b border-gray-800/50">
            Sideboard ({sideboardMatches.length})
          </div>
          <FlatColumnLayout
            cards={sideboardMatches}
            renderCard={(card) => <SearchResultRow key={`sideboard-${card.id}`} card={card} />}
          />
        </div>
      )}

      {/* New results section — excludes synergy cards to avoid duplicates */}
      {filteredNewResults.length > 0 && (
        <div>
          <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-gray-900/60 border-b border-gray-800/50">
            Results ({filteredNewResults.length})
          </div>
          <FlatColumnLayout
            cards={filteredNewResults}
            renderCard={(card) => <SearchResultRow key={`result-${card.id}`} card={card} />}
          />
        </div>
      )}
    </div>
  );
}
